import {
  Application,
  Container,
  Graphics,
  Sprite,
  Text,
  type FederatedPointerEvent,
  type Spritesheet,
} from "pixi.js";
import palette from "../ui/theme/palette.json";
import { managePresentation, renderResolution } from "../lib/graphics.js";
import type { Point } from "./iso.js";
import { loadVillageArt } from "./VillageView.js";
import {
  WorldLandscape,
  loadWorldMaterial,
  MAP_RADIUS,
  MAP_TILE,
  landscapeHash,
} from "./worldLandscape.js";

/**
 * The world map (S09, first version Sep 2026): every village on its square,
 * yours in gold.
 *
 * Phase 0 has no travel time, so the map is for finding and choosing — who is
 * out there, which barbarians are near, what you have already beaten. It is
 * flat and top-down (north up), the way strategy players read a map, with the
 * village pictures from the village screen as markers.
 *
 * Like the village view, it knows nothing about attacking: it reports taps by
 * village id ("home" for your own) and React decides what they mean.
 */

export interface MapVillage {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly name: string;
  readonly kind: "home" | "player" | "npc";
  /** Barbarians only: easy / medium / hard / brutal. */
  readonly difficulty: string | null;
  /** You have already broken through it. */
  readonly beaten: boolean;
}

/** The square players are placed in (server MAP_RADIUS). */
export { MAP_RADIUS } from "./worldLandscape.js";
/** One map square, in world pixels. */
const TILE = MAP_TILE;
const MAX_SCALE = 2.2;
const TAP_SLOP = 8;

export const DIFFICULTY_COLOR: Record<string, string> = {
  easy: palette.ok,
  medium: palette.gold,
  hard: palette.warn,
  brutal: palette.danger,
};
export const PLAYER_COLOR = "#5b8fd9";

function hex(css: string): number {
  return Number.parseInt(css.replace("#", ""), 16);
}

interface MapPin {
  village: MapVillage;
  pin: Container;
  detail: Container;
  symbol: Container;
  label: Container;
  banner: Graphics;
  smoke: Graphics[];
  color: number;
  size: number;
  x: number;
  y: number;
}

/** Map square → world point (square centre). North is up. */
export function squareToWorld(x: number, y: number): Point {
  return { x: x * TILE, y: -y * TILE };
}

export class MapView {
  private releasePresentation: (() => void) | null = null;
  readonly app: Application;
  private readonly world = new Container();
  private readonly ground = new Graphics();
  private readonly landscape: WorldLandscape;
  private readonly markers = new Container();
  private readonly pins: MapPin[] = [];
  private ring: Graphics | null = null;
  private readonly labels = new Container();

  private readonly art: Spritesheet | null;
  private villages: readonly MapVillage[] = [];
  private selected: string | null = null;
  private onTapFn: ((id: string | null) => void) | null = null;
  private destroyed = false;
  private readonly reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  private life = 0;
  private lastMotion = -Infinity;
  private screenWidth = 0;
  private screenHeight = 0;
  private readonly resize = (): void => {
    this.world.x += (this.app.screen.width - this.screenWidth) / 2;
    this.world.y += (this.app.screen.height - this.screenHeight) / 2;
    this.screenWidth = this.app.screen.width;
    this.screenHeight = this.app.screen.height;
    this.clamp();
    this.fitLabels();
  };

  private readonly pointers = new Map<number, Point>();
  private pressedAt: Point | null = null;
  private dragged = false;
  private gesture: { distance: number; midpoint: Point } | null = null;

  private constructor(
    app: Application,
    art: Spritesheet | null,
    material: HTMLImageElement | null
  ) {
    this.app = app;
    this.art = art;
    this.landscape = new WorldLandscape(material, art);
    this.markers.sortableChildren = true;
    this.world.addChild(this.landscape, this.ground, this.markers, this.labels);
    app.stage.addChild(this.world);
    this.drawGround();
    this.bindPointer();
    this.screenWidth = app.screen.width;
    this.screenHeight = app.screen.height;
    app.renderer.on("resize", this.resize);
    app.ticker.add(() => this.animate());
  }

  static async mount(host: HTMLElement): Promise<MapView> {
    const app = new Application();
    await app.init({
      background: palette.bg,
      antialias: true,
      resizeTo: host,
      resolution: renderResolution(),
      autoDensity: true,
      preference: "webgl",
    });
    host.appendChild(app.canvas);
    const [art, material] = await Promise.all([loadVillageArt(), loadWorldMaterial()]);
    const view = new MapView(app, art, material);
    view.releasePresentation = managePresentation(app);
    return view;
  }

  onTap(fn: (id: string | null) => void): void {
    this.onTapFn = fn;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.releasePresentation?.();
    this.app.renderer.off("resize", this.resize);
    // `{ removeView: true }`, NOT `true`: in Pixi 8, `true` also releases the
    // GLOBAL texture pool every canvas on the page shares. Closing the village
    // behind the tutorial then broke the tutorial board mid-step — it went dark
    // at the gate (friends test #2 prep, Sep 2026).
    this.app.destroy({ removeView: true }, { children: true });
  }

  // --- content ---------------------------------------------------------------

  setVillages(villages: readonly MapVillage[]): void {
    if (this.destroyed) return;
    this.villages = villages;
    this.landscape.setVillages(villages);
    this.drawMarkers();
  }

  select(id: string | null): void {
    if (this.destroyed) return;
    this.selected = id;
    this.drawMarkers();
  }

  /**
   * Open on home, close up: your village and the few squares around it
   * (playtest, 29 Sep 2026 — the map used to open zoomed out far enough to
   * take in the nearest barbarians, which on a phone made every village a
   * dot). Zooming out is the player's choice, with a pinch or the − button.
   */
  frame(): void {
    const home = this.villages.find((v) => v.kind === "home");
    this.stopFlight();
    this.world.scale.set(this.homeScale());
    this.centreOn(home?.x ?? 0, home?.y ?? 0);
  }

  /**
   * Back to my village, from wherever the player has wandered — at the home
   * zoom, not the current one. It used to only re-centre at the current
   * zoom, and zoomed out that moved nothing visible, so the button read as
   * broken (playtest, 29 Sep 2026). A short glide, so the eye can follow.
   */
  goHome(x: number, y: number): void {
    if (this.destroyed) return;
    const scale = this.homeScale();
    const c = squareToWorld(x, y);
    const to = {
      scale,
      x: this.app.screen.width / 2 - c.x * scale,
      y: this.app.screen.height / 2 - c.y * scale,
    };
    const from = { scale: this.world.scale.x, x: this.world.x, y: this.world.y };
    if (this.reducedMotion.matches) {
      this.world.scale.set(scale);
      this.centreOn(x, y);
      return;
    }
    this.stopFlight();
    const started = performance.now();
    const step = (): void => {
      if (this.destroyed) return;
      const t = Math.min(1, (performance.now() - started) / 450);
      const k = 1 - (1 - t) ** 3;
      this.world.scale.set(from.scale + (to.scale - from.scale) * k);
      this.world.x = from.x + (to.x - from.x) * k;
      this.world.y = from.y + (to.y - from.y) * k;
      this.fitLabels();
      if (t >= 1) {
        this.stopFlight();
        this.centreOn(x, y);
      }
    };
    this.flight = step;
    this.app.ticker.add(step);
  }

  private flight: (() => void) | null = null;

  private stopFlight(): void {
    if (!this.flight) return;
    this.app.ticker.remove(this.flight);
    this.flight = null;
  }

  /** The close-up zoom: about this many squares across the narrow side. */
  private homeScale(): number {
    const span = 13 * TILE;
    return clamp(
      Math.min(this.app.screen.width, this.app.screen.height) / span,
      this.minScale(),
      MAX_SCALE
    );
  }

  /** Put a square in the middle of the screen. */
  centreOn(x: number, y: number): void {
    const c = squareToWorld(x, y);
    const s = this.world.scale.x;
    this.world.x = this.app.screen.width / 2 - c.x * s;
    this.world.y = this.app.screen.height / 2 - c.y * s;
    this.clamp();
    this.fitLabels();
  }

  // --- drawing -----------------------------------------------------------------

  private drawGround(): void {
    const half = (MAP_RADIUS + 0.5) * TILE;
    for (let i = 0; i <= 2 * MAP_RADIUS + 1; i++) {
      const p = -half + i * TILE;
      this.ground.moveTo(p, -half).lineTo(p, half);
      this.ground.moveTo(-half, p).lineTo(half, p);
    }
    this.ground.stroke({ color: 0xf1e3bd, width: 0.6, alpha: 0.045 });
    this.ground
      .rect(-half, -half, half * 2, half * 2)
      .stroke({ color: hex(palette.gold), width: 3, alpha: 0.5 });
    this.ground.eventMode = "none";
  }

  private drawMarkers(): void {
    for (const child of this.markers.removeChildren()) child.destroy({ children: true });
    for (const child of this.labels.removeChildren()) child.destroy({ children: true });
    this.pins.length = 0;
    this.ring = null;
    for (const v of this.villages) {
      const w = squareToWorld(v.x, v.y);
      const pin = new Container(),
        detail = new Container(),
        symbol = new Container();
      pin.position.set(w.x, w.y);
      pin.zIndex = -v.y;
      pin.addChild(detail, symbol);
      const color = hex(
        v.kind === "home"
          ? palette.gold
          : v.kind === "player"
            ? PLAYER_COLOR
            : (DIFFICULTY_COLOR[v.difficulty ?? ""] ?? palette.danger)
      );
      const selected = v.id === this.selected;
      detail.addChild(
        new Graphics()
          .ellipse(3, 9, 76, 33)
          .fill({ color: 0x273b2a, alpha: 0.3 })
          .ellipse(0, 5, 70, 31)
          .fill({ color: 0xc2ad79, alpha: 0.38 })
      );
      if (selected || v.kind === "home") {
        const ring = new Graphics()
          .ellipse(0, 8, 80, 39)
          .stroke({ color: 0xf5d987, width: selected ? 2.5 : 1.4, alpha: selected ? 1 : 0.6 });
        detail.addChild(ring);
        if (selected) this.ring = ring;
      }
      const building = (name: string, x: number, y: number, width: number): void => {
        const texture = this.art?.textures[name];
        if (!texture) return;
        const sprite = new Sprite(texture);
        sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
        sprite.position.set(x, y);
        sprite.scale.set(width / texture.width);
        detail.addChild(sprite);
      };
      if (v.kind === "home") {
        building("grain_farm", -41, -15, 60);
        building("residence", 40, -17, 48);
        building("main_building", 0, 0, 106);
        building("warehouse", -36, 29, 48);
        building("shrine", 39, 24, 49);
      } else if (v.kind === "player") {
        building("residence", -34, -19, 44);
        building("barracks", 36, -9, 62);
        building("main_building", 0, 5, 86);
        building("wall", -35, 27, 42);
        building("wall", 33, 34, 37);
      } else {
        building("woodcutter", -33, -8, 60);
        building("stable", 32, -4, 59);
        building("barracks", 0, 16, 62);
        building("decor_fence", -42, 30, 25);
        building("decor_fence", 38, 34, 25);
        detail.addChild(
          new Graphics().ellipse(5, 29, 6, 3).fill(0x574730).ellipse(5, 25, 2.5, 4).fill(0xefbb67)
        );
      }
      const banner = new Graphics();
      detail.addChild(
        new Graphics().moveTo(57, 10).lineTo(57, -40).stroke({ color: 0x514734, width: 2 }),
        banner
      );
      const smoke: Graphics[] = [];
      for (let i = 0; i < 4; i++) {
        const puff = new Graphics().ellipse(0, 0, 4, 3).fill(0xe9dfbc);
        detail.addChild(puff);
        smoke.push(puff);
      }
      const mark = v.kind === "home" ? "◆" : v.kind === "player" ? "●" : "▲";
      symbol.addChild(
        new Graphics()
          .circle(0, 0, 12)
          .fill({ color: 0x1a3025, alpha: 0.96 })
          .stroke({ color: selected ? 0xf5d987 : color, width: selected ? 2 : 1 })
      );
      const glyph = new Text({
        text: mark,
        style: { fontFamily: "system-ui, sans-serif", fontSize: 16, fill: color },
      });
      glyph.anchor.set(0.5);
      symbol.addChild(glyph);
      this.markers.addChild(pin);

      const label = new Container();
      const name = v.name.length > 24 ? `${v.name.slice(0, 23)}…` : v.name;
      const text = new Text({
        text: `${mark} ${name}${v.beaten ? " ✓" : ""}`,
        style: {
          fontFamily: "system-ui, sans-serif",
          fontSize: 12,
          fontWeight: "600",
          fill: 0xf4e5c5,
        },
      });
      text.anchor.set(0.5, 0);
      const bg = new Graphics()
        .roundRect(-text.width / 2 - 7, -4, text.width + 14, text.height + 8, 5)
        .fill({ color: selected ? 0x35422a : 0x18271d, alpha: 0.96 })
        .stroke({ color: selected ? 0xedc479 : 0x7c7852, width: selected ? 1.5 : 0.8 });
      const caption =
        v.kind === "home"
          ? "Your village"
          : v.kind === "player"
            ? "Player village"
            : `${v.difficulty ?? "Barbarian"}${v.beaten ? " · Breached" : ""}`;
      const sub = new Text({
        text: caption,
        style: { fontFamily: "system-ui, sans-serif", fontSize: 11, fill: 0xf4e5c5 },
      });
      sub.anchor.set(0.5, 0);
      sub.y = text.height + 9;
      const subBg = new Graphics()
        .roundRect(-sub.width / 2 - 4, sub.y - 1, sub.width + 8, sub.height + 2, 3)
        .fill({ color: 0x172719, alpha: 0.84 });
      label.addChild(bg, text, subBg, sub);
      this.labels.addChild(label);
      // At adjacent map squares, keep the artwork within its own settlement's space.
      const nearest = Math.min(
        3,
        ...this.villages.filter((q) => q.id !== v.id).map((q) => dist(q, v))
      );
      const size = clamp((nearest * TILE) / 170, 0.25, 0.62);
      this.pins.push({
        village: v,
        pin,
        detail,
        symbol,
        label,
        banner,
        smoke,
        color,
        size,
        x: w.x,
        y: w.y,
      });
    }
    this.fitLabels();
    this.updateMotion(this.reducedMotion.matches ? 0 : this.life);
  }

  /** Artwork at close zoom; compact symbols at region scale. Labels never shrink. */
  private fitLabels(): void {
    const s = this.world.scale.x;
    const width = this.app.screen.width,
      height = this.app.screen.height;
    this.ground.visible = s > 1.5;
    this.landscape.fit(
      -this.world.x / s,
      -this.world.y / s,
      (width - this.world.x) / s,
      (height - this.world.y) / s
    );
    const boxes: { x: number; y: number; w: number; h: number; markerId?: string }[] = [];
    // Reserve the compass, legend, and the three real DOM map controls.
    boxes.push(
      { x: width - 64, y: 0, w: 64, h: 175 },
      { x: 0, y: 0, w: 48, h: 65 },
      { x: 0, y: height - 42, w: width, h: 42 }
    );
    // Never cover another settlement picture with a neighbouring nameplate.
    for (const p of this.pins) {
      const detailed = !!this.art && p.size * s >= 0.24;
      const k = p.size * s;
      const px = this.world.x + p.x * s,
        py = this.world.y + p.y * s;
      boxes.push(
        detailed
          ? { markerId: p.village.id, x: px - 70 * k, y: py - 70 * k, w: 140 * k, h: 100 * k }
          : { markerId: p.village.id, x: px - 12, y: py - 12, w: 24, h: 24 }
      );
    }
    const ordered = [...this.pins].sort((a, b) => {
      const rank = (p: MapPin) =>
        p.village.id === this.selected ? 0 : p.village.kind === "home" ? 1 : 2;
      return rank(a) - rank(b) || a.village.id.localeCompare(b.village.id);
    });
    for (const p of ordered) {
      const detailed = !!this.art && p.size * s >= 0.24;
      p.detail.visible = detailed;
      p.symbol.visible = !detailed;
      p.pin.scale.set(detailed ? p.size : 1 / s);
      p.label.scale.set(1 / s);
      p.label.position.set(p.x, p.y + (detailed ? 48 * p.size : 20 / s));
      const px = this.world.x + p.x * s,
        py = this.world.y + p.y * s;
      p.pin.visible = px > -100 && px < width + 100 && py > -100 && py < height + 100;
      const w = p.label.width * s,
        h = p.label.height * s;
      const labelX = clamp(px, w / 2 + 6, width - w / 2 - 6);
      p.label.x = (labelX - this.world.x) / s;
      const box = { x: labelX - w / 2, y: this.world.y + p.label.y * s - 4, w, h };
      p.label.visible =
        p.pin.visible &&
        box.x >= 5 &&
        box.x + w < width - 5 &&
        box.y >= 5 &&
        box.y + h < height - 5 &&
        !boxes.some(
          (b) =>
            b.markerId !== p.village.id &&
            box.x < b.x + b.w + 4 &&
            box.x + w + 4 > b.x &&
            box.y < b.y + b.h + 4 &&
            box.y + h + 4 > b.y
        );
      if (p.label.visible) boxes.push(box);
    }
  }

  private updateMotion(t: number): void {
    for (const p of this.pins) {
      if (!p.pin.visible || !p.detail.visible) continue;
      const phase = t * 1.8 + landscapeHash(p.x, p.y) * 6;
      const bend = Math.sin(phase) * 2;
      p.banner
        .clear()
        .moveTo(57, -40)
        .bezierCurveTo(65, -40 + bend, 74, -35 - bend, 82, -38)
        .lineTo(80, -23)
        .bezierCurveTo(72, -20 - bend, 64, -26, 57, -25)
        .closePath()
        .fill(p.color);
      for (let i = 0; i < p.smoke.length; i++) {
        const puff = p.smoke[i]!;
        const k = (t * 0.1 + i / 4 + landscapeHash(p.x, p.y, 2)) % 1;
        const npc = p.village.kind === "npc";
        puff.position.set(
          (npc ? 5 : -3) + Math.sin(k * 4 + i) * 4 + k * 10,
          (npc ? 18 : -54) - k * 42
        );
        puff.scale.set(0.5 + k * 1.8);
        puff.alpha = (1 - k) * 0.17;
      }
    }
    if (this.ring)
      this.ring.alpha = this.reducedMotion.matches ? 1 : 0.85 + Math.sin(t * 1.5) * 0.15;
  }

  private animate(): void {
    if (this.destroyed || document.hidden) return;
    if (!this.reducedMotion.matches) this.life += Math.min(this.app.ticker.deltaMS, 50) / 1000;
    if (this.app.ticker.lastTime - this.lastMotion < 33) return;
    this.lastMotion = this.app.ticker.lastTime;
    this.updateMotion(this.reducedMotion.matches ? 0 : this.life);
  }

  // --- input -------------------------------------------------------------------

  private bindPointer(): void {
    const stage = this.app.stage;
    stage.eventMode = "static";
    stage.hitArea = { contains: () => true };

    stage.on("pointerdown", (e: FederatedPointerEvent) => {
      // A finger on the map takes over from a glide home.
      this.stopFlight();
      this.pointers.set(e.pointerId, { x: e.global.x, y: e.global.y });
      if (this.pointers.size === 1) {
        this.pressedAt = { x: e.global.x, y: e.global.y };
        this.dragged = false;
      } else {
        this.gesture = this.readGesture();
        this.dragged = true;
      }
    });

    stage.on("pointermove", (e: FederatedPointerEvent) => {
      const prev = this.pointers.get(e.pointerId);
      if (!prev) return;
      const now = { x: e.global.x, y: e.global.y };
      this.pointers.set(e.pointerId, now);
      if (this.pointers.size > 1) {
        const next = this.readGesture();
        if (this.gesture && next && this.gesture.distance > 0) {
          this.zoomAround(next.midpoint, next.distance / this.gesture.distance);
          this.panBy(
            next.midpoint.x - this.gesture.midpoint.x,
            next.midpoint.y - this.gesture.midpoint.y
          );
        }
        this.gesture = next;
        return;
      }
      if (
        this.pressedAt &&
        Math.hypot(now.x - this.pressedAt.x, now.y - this.pressedAt.y) > TAP_SLOP
      ) {
        this.dragged = true;
      }
      if (this.dragged) this.panBy(now.x - prev.x, now.y - prev.y);
    });

    const end = (e: FederatedPointerEvent): void => {
      this.pointers.delete(e.pointerId);
      this.gesture = this.pointers.size > 1 ? this.readGesture() : null;
      if (this.pointers.size > 0) return;
      if (this.pressedAt && !this.dragged)
        this.onTapFn?.(this.villageAt({ x: e.global.x, y: e.global.y }));
      this.pressedAt = null;
    };
    stage.on("pointerup", end);
    stage.on("pointerupoutside", end);

    this.app.canvas.addEventListener(
      "wheel",
      (event: WheelEvent) => {
        event.preventDefault();
        const rect = this.app.canvas.getBoundingClientRect();
        this.zoomAround(
          { x: event.clientX - rect.left, y: event.clientY - rect.top },
          event.deltaY < 0 ? 1.12 : 1 / 1.12
        );
      },
      { passive: false }
    );
  }

  /**
   * The village nearest a tap, if it is close enough. Generous on purpose: at
   * the zoom that shows a region, a village is a small target for a finger.
   */
  villageAt(global: Point): string | null {
    let best: string | null = null;
    let bestDistance = Infinity;
    const scale = this.world.scale.x;
    for (const p of this.pins) {
      if (p.label.visible && p.label.getBounds().containsPoint(global.x, global.y))
        return p.village.id;
      // The picture stands above its square: aim at the middle of it.
      const centre = this.world.toGlobal({ x: p.x, y: p.y - (p.detail.visible ? 12 * p.size : 0) });
      const d = Math.hypot(centre.x - global.x, centre.y - global.y);
      const reach = Math.max(22, p.detail.visible ? 76 * p.size * scale : 22);
      if (d < reach && d < bestDistance) {
        best = p.village.id;
        bestDistance = d;
      }
    }
    return best;
  }

  /** Where a village is on the page, for tests. */
  screenOf(id: string): Point | null {
    const v = this.villages.find((q) => q.id === id);
    if (!v) return null;
    const p = this.world.toGlobal(squareToWorld(v.x, v.y));
    const rect = this.app.canvas.getBoundingClientRect();
    return { x: rect.left + p.x, y: rect.top + p.y };
  }

  private readGesture(): { distance: number; midpoint: Point } | null {
    const [a, b] = [...this.pointers.values()];
    if (!a || !b) return null;
    return {
      distance: Math.hypot(a.x - b.x, a.y - b.y),
      midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  }

  /** Zoomed all the way out, the whole world fits. */
  private minScale(): number {
    const size = (2 * MAP_RADIUS + 1) * TILE;
    return Math.min(this.app.screen.width / size, this.app.screen.height / size);
  }

  private zoomAround(screen: Point, factor: number): void {
    const before = this.world.scale.x;
    const after = clamp(before * factor, this.minScale(), MAX_SCALE);
    if (after === before) return;
    const ratio = after / before;
    this.world.scale.set(after);
    this.world.x = screen.x - (screen.x - this.world.x) * ratio;
    this.world.y = screen.y - (screen.y - this.world.y) * ratio;
    this.clamp();
    this.fitLabels();
  }

  zoomBy(factor: number): void {
    this.zoomAround({ x: this.app.screen.width / 2, y: this.app.screen.height / 2 }, factor);
  }

  private panBy(dx: number, dy: number): void {
    this.world.x += dx;
    this.world.y += dy;
    this.clamp();
    this.fitLabels();
  }

  /** Keep part of the world on screen, whatever the finger does. */
  private clamp(): void {
    const s = this.world.scale.x;
    const half = (MAP_RADIUS + 0.5) * TILE * s;
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    const margin = 80;
    this.world.x = clamp(this.world.x, w - margin - half, margin + half);
    this.world.y = clamp(this.world.y, h - margin - half, margin + half);
  }
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
